import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============= POSTS API =============

export const getPosts = async (status = null, forSale = null) => {
  try {
    const params = {};
    if (status) params.status = status;
    if (forSale !== null) params.for_sale = forSale ? 'true' : 'false';
    const response = await axios.get(`${API}/posts`, { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPost = async (postId) => {
  try {
    const response = await axios.get(`${API}/posts/${postId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createPost = async (postData) => {
  try {
    const response = await axios.post(`${API}/posts`, postData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updatePost = async (postId, postData) => {
  try {
    const response = await axios.put(`${API}/posts/${postId}`, postData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deletePost = async (postId) => {
  try {
    const response = await axios.delete(`${API}/posts/${postId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ============= COMMENTS API =============

export const getComments = async (postId) => {
  try {
    const response = await axios.get(`${API}/comments/${postId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createComment = async (commentData) => {
  try {
    const response = await axios.post(`${API}/comments`, commentData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteComment = async (commentId) => {
  try {
    const response = await axios.delete(`${API}/comments/${commentId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ============= LIKES API =============

export const toggleLike = async (postId) => {
  try {
    const response = await axios.post(`${API}/likes/${postId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getLikes = async (postId) => {
  try {
    const response = await axios.get(`${API}/likes/${postId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const checkUserLike = async (postId) => {
  try {
    const response = await axios.get(`${API}/likes/user/${postId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ============= TIERS API =============

export const getTiers = async () => {
  try {
    const response = await axios.get(`${API}/tiers`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createTier = async (tierData) => {
  try {
    const response = await axios.post(`${API}/tiers`, tierData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ============= ADMIN API =============

export const getAllUsers = async () => {
  try {
    const response = await axios.get(`${API}/admin/users`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateUser = async (userId, userData) => {
  try {
    const response = await axios.put(`${API}/admin/users/${userId}`, userData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const banUser = async (userId, banType, reason = '') => {
  const response = await axios.post(`${API}/admin/users/${userId}/ban`, {
    banType,
    reason,
  });
  return response.data;
};

export const unbanUser = async (userId) => {
  const response = await axios.post(`${API}/admin/users/${userId}/unban`);
  return response.data;
};

export const adminResetUserPassword = async (userId, newPassword) => {
  const response = await axios.post(`${API}/admin/reset-user-password`, { user_id: userId, new_password: newPassword });
  return response.data;
};

export const createReviewerAccount = async ({ email, displayName, password, days = 30 }) => {
  const response = await axios.post(`${API}/admin/reviewer-accounts`, { email, displayName, password, days });
  return response.data;
};

// ============= FILE UPLOAD API =============

export const uploadImage = async (file, onProgress = null) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    };
    
    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      };
    }
    
    const response = await axios.post(`${API}/upload/image`, formData, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const uploadMultipleImages = async (files, onProgress = null) => {
  try {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    };
    
    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      };
    }
    
    const response = await axios.post(`${API}/upload/images`, formData, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteUploadedFile = async (url) => {
  try {
    const response = await axios.delete(`${API}/upload`, { params: { url } });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getStorageStats = async () => {
  try {
    const response = await axios.get(`${API}/upload/stats`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ============= PAYMENT API =============

export const getPaymentTiers = async () => {
  try {
    const response = await axios.get(`${API}/payments/tiers`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createSubscriptionCheckout = async (tierId, discountCode = null, billingInterval = 'month') => {
  try {
    const payload = {
      tier_id: tierId,
      origin_url: window.location.origin,
      billing_interval: billingInterval
    };
    if (discountCode) {
      payload.discount_code = discountCode;
    }
    const response = await axios.post(`${API}/payments/checkout/subscription`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const cancelSubscription = async () => {
  try {
    const response = await axios.post(`${API}/payments/cancel-subscription`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteAccount = async () => {
  const response = await axios.delete(`${API}/auth/me`);
  return response.data;
};


export const createPostPurchaseCheckout = async (postId) => {
  try {
    const response = await axios.post(`${API}/payments/checkout/post`, {
      post_id: postId,
      origin_url: window.location.origin
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createCollectionPurchaseCheckout = async (collectionId) => {
  try {
    const response = await axios.post(`${API}/payments/checkout/collection`, {
      collection_id: collectionId,
      origin_url: window.location.origin
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPaymentStatus = async (sessionId) => {
  try {
    const response = await axios.get(`${API}/payments/status/${sessionId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getUserTransactions = async () => {
  try {
    const response = await axios.get(`${API}/payments/transactions`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getUserPurchases = async () => {
  try {
    const response = await axios.get(`${API}/payments/purchases`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const validateDiscountCode = async (code, tierId, billingInterval = 'month') => {
  try {
    const response = await axios.post(`${API}/payments/validate-discount`, null, {
      params: { code, tier_id: tierId, billing_interval: billingInterval }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Admin payment functions
export const getAllTransactions = async () => {
  try {
    const response = await axios.get(`${API}/admin/payments/transactions`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPaymentStats = async () => {
  try {
    const response = await axios.get(`${API}/admin/payments/stats`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const startPaypalReconcile = async (dryRun = true) => {
  const response = await axios.post(`${API}/payments/paypal/reconcile-pending?dry_run=${dryRun}`);
  return response.data;
};

export const getPaypalReconcileStatus = async () => {
  const response = await axios.get(`${API}/payments/paypal/reconcile-pending/status`);
  return response.data;
};

export const updateTierPricing = async (tierId, tierUpdate) => {
  try {
    const response = await axios.put(`${API}/admin/payments/tiers/${tierId}`, tierUpdate);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAdminTiers = async () => {
  try {
    const response = await axios.get(`${API}/admin/payments/tiers`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createDiscountCode = async (discountData) => {
  try {
    const response = await axios.post(`${API}/admin/discounts`, discountData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllDiscountCodes = async () => {
  try {
    const response = await axios.get(`${API}/admin/discounts`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteDiscountCode = async (code) => {
  try {
    const response = await axios.delete(`${API}/admin/discounts/${code}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ============= COLLECTIONS API =============

export const getCollections = async (publishedOnly = true) => {
  try {
    const response = await axios.get(`${API}/collections`, { params: { published_only: publishedOnly } });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCollection = async (collectionId) => {
  try {
    const response = await axios.get(`${API}/collections/${collectionId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createCollection = async (collectionData) => {
  try {
    const response = await axios.post(`${API}/collections`, collectionData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateCollection = async (collectionId, collectionData) => {
  try {
    const response = await axios.put(`${API}/collections/${collectionId}`, collectionData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteCollection = async (collectionId) => {
  try {
    const response = await axios.delete(`${API}/collections/${collectionId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const addPostToCollection = async (collectionId, postId) => {
  try {
    const response = await axios.post(`${API}/collections/${collectionId}/posts/${postId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const removePostFromCollection = async (collectionId, postId) => {
  try {
    const response = await axios.delete(`${API}/collections/${collectionId}/posts/${postId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCollectionPosts = async (collectionId) => {
  try {
    const response = await axios.get(`${API}/collections/${collectionId}/posts`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ============= SITE SETTINGS API =============

export const getSiteSettings = async () => {
  try {
    const response = await axios.get(`${API}/settings`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const saveSiteSettings = async (settingsData) => {
  try {
    const response = await axios.post(`${API}/settings`, settingsData);
    return response.data;
  } catch (error) {
    throw error;
  }
};


// ============= DIRECT MESSAGES API =============

export const getConversations = async () => {
  try {
    const response = await axios.get(`${API}/messages/conversations`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createConversation = async (recipientId) => {
  try {
    const response = await axios.post(`${API}/messages/conversations`, null, {
      params: { recipient_id: recipientId }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getConversationMessages = async (conversationId) => {
  try {
    const response = await axios.get(`${API}/messages/conversations/${conversationId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const sendMessage = async (conversationId, content) => {
  try {
    const response = await axios.post(`${API}/messages/conversations/${conversationId}`, null, {
      params: { content }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getUnreadMessageCount = async () => {
  try {
    const response = await axios.get(`${API}/messages/unread-count`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getOwnerInfo = async () => {
  try {
    const response = await axios.get(`${API}/messages/owner`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ============= GLOBAL CHAT API =============

export const getChatMessages = async (limit = 50, before = null) => {
  try {
    const params = { limit };
    if (before) params.before = before;
    const response = await axios.get(`${API}/chat/messages`, { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const sendChatMessage = async (content) => {
  try {
    const response = await axios.post(`${API}/chat/messages`, null, {
      params: { content }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteChatMessage = async (messageId) => {
  try {
    const response = await axios.delete(`${API}/chat/messages/${messageId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Chat rooms (channel-based chat panel)
export const getChatRooms = async () => (await axios.get(`${API}/chat/rooms`)).data;
export const getChatUnread = async () => (await axios.get(`${API}/chat/unread`)).data;
export const getRoomMessages = async (roomId, params = {}) =>
  (await axios.get(`${API}/chat/rooms/${roomId}/messages`, { params })).data;
export const sendRoomMessage = async (roomId, content, replyTo = null, mentionIds = [], image = null) => {
  const params = { content };
  if (replyTo) params.reply_to = replyTo;
  if (mentionIds.length) params.mentions = mentionIds.join(',');
  if (image?.url) { params.image_url = image.url; params.image_thumb_url = image.thumbUrl || image.url; }
  return (await axios.post(`${API}/chat/rooms/${roomId}/messages`, null, { params })).data;
};
export const uploadChatImage = async (roomId, file) => {
  const form = new FormData();
  form.append('file', file);
  return (await axios.post(`${API}/chat/rooms/${roomId}/image`, form, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
};
export const editRoomMessage = async (roomId, messageId, content, mentionIds = []) =>
  (await axios.put(`${API}/chat/rooms/${roomId}/messages/${messageId}`, null, { params: { content, mentions: mentionIds.join(',') } })).data;
export const markRoomRead = async (roomId, messageId = null) =>
  (await axios.post(`${API}/chat/rooms/${roomId}/read`, null, { params: messageId ? { message_id: messageId } : {} })).data;
export const getRoomMembers = async (roomId) => (await axios.get(`${API}/chat/rooms/${roomId}/members`)).data;
export const toggleReaction = async (roomId, messageId, emoji) =>
  (await axios.post(`${API}/chat/rooms/${roomId}/messages/${messageId}/react`, null, { params: { emoji } })).data;
export const getRoomMentions = async (roomId) => (await axios.get(`${API}/chat/rooms/${roomId}/mentions`)).data;
export const sendTyping = async (roomId) => (await axios.post(`${API}/chat/rooms/${roomId}/typing`)).data;
export const getTyping = async (roomId) => (await axios.get(`${API}/chat/rooms/${roomId}/typing`)).data;
export const togglePin = async (roomId, messageId) => (await axios.post(`${API}/chat/rooms/${roomId}/messages/${messageId}/pin`)).data;
export const getRoomPins = async (roomId) => (await axios.get(`${API}/chat/rooms/${roomId}/pins`)).data;

export const getOnlineStatus = async () => {
  try {
    const response = await axios.get(`${API}/chat/online`);
    return response.data;
  } catch (error) {
    throw error;
  }
};


// ============= AVATAR API =============

export const uploadAvatar = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}/upload/avatar`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteAvatar = async () => {
  try {
    const response = await axios.delete(`${API}/upload/avatar`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateProfile = async (profileData) => {
  try {
    const response = await axios.put(`${API}/auth/me`, profileData);
    return response.data;
  } catch (error) {
    throw error;
  }
};


// ============= PUSH NOTIFICATIONS =============

export const registerPushToken = async (token) => {
  try {
    const response = await axios.post(`${API}/notifications/register`, { token });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const unregisterPushToken = async (token) => {
  try {
    const response = await axios.post(`${API}/notifications/unregister`, { token });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getNotificationSettings = async () => {
  try {
    const response = await axios.get(`${API}/notifications/settings`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateNotificationSettings = async (settings) => {
  try {
    const response = await axios.put(`${API}/notifications/settings`, settings);
    return response.data;
  } catch (error) {
    throw error;
  }
};


// ============= ADMIN ANALYTICS =============

export const getAdminAnalytics = async () => {
  try {
    const response = await axios.get(`${API}/admin/analytics`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const reconcilePastDue = async (applyFixes = false) => {
  const response = await axios.post(
    `${API}/admin/reconcile-past-due`,
    null,
    { params: { apply_fixes: applyFixes } }
  );
  return response.data;
};

export const auditDiscountRecipients = async () => {
  const response = await axios.post(`${API}/admin/audit-discount-recipients`);
  return response.data;
};

// ============= GAMIFICATION API =============

export const getMyRank = async () => {
  const response = await axios.get(`${API}/gamification/me`);
  return response.data;
};

export const getPublicMilestones = async () => {
  const response = await axios.get(`${API}/gamification/milestones`);
  return response.data;
};

export const acknowledgeRankUp = async () => {
  const response = await axios.post(`${API}/gamification/acknowledge-rank`);
  return response.data;
};

export const claimReward = async (milestoneId, note = '', selectedOption = '') => {
  const response = await axios.post(`${API}/gamification/claim`, { milestoneId, note, selectedOption });
  return response.data;
};

export const adminGetMilestones = async () => {
  const response = await axios.get(`${API}/admin/gamification/milestones`);
  return response.data;
};

export const adminCreateMilestone = async (data) => {
  const response = await axios.post(`${API}/admin/gamification/milestones`, data);
  return response.data;
};

export const adminUpdateMilestone = async (id, data) => {
  const response = await axios.put(`${API}/admin/gamification/milestones/${id}`, data);
  return response.data;
};

export const adminDeleteMilestone = async (id) => {
  const response = await axios.delete(`${API}/admin/gamification/milestones/${id}`);
  return response.data;
};

export const adminGetClaims = async (statusFilter = null) => {
  const params = statusFilter ? { status_filter: statusFilter } : {};
  const response = await axios.get(`${API}/admin/gamification/claims`, { params });
  return response.data;
};

export const adminFulfillClaim = async (claimId) => {
  const response = await axios.post(`${API}/admin/gamification/claims/${claimId}/fulfill`);
  return response.data;
};


// ============= PASSWORD RESET API =============

export const requestPasswordReset = async (email) => {
  const response = await axios.post(`${API}/auth/forgot-password`, { email });
  return response.data;
};

export const resetPassword = async (token, new_password) => {
  const response = await axios.post(`${API}/auth/reset-password`, { token, new_password });
  return response.data;
};

// ============= CRYPTO PAYMENT API =============

export const createCryptoCheckout = async (tierId, discountCode = null) => {
  const response = await axios.post(`${API}/payments/crypto/checkout`, {
    tier_id: tierId,
    origin_url: window.location.origin,
    discount_code: discountCode || undefined
  });
  return response.data;
};

export const getCryptoPaymentStatus = async (paymentId) => {
  const response = await axios.get(`${API}/payments/crypto/status/${paymentId}`);
  return response.data;
};

export const getCryptoCurrencies = async () => {
  const response = await axios.get(`${API}/payments/crypto/currencies`);
  return response.data;
};

export const getCryptoEstimate = async (amount, currency) => {
  const response = await axios.get(`${API}/payments/crypto/estimate?amount=${amount}&currency=${currency}`);
  return response.data;
};

// ============= PAYPAL PAYMENT API =============

export const getPayPalConfig = async () => {
  const response = await axios.get(`${API}/payments/paypal/config`);
  return response.data;
};

export const createPayPalSubscription = async (tierId, billingInterval = 'month', discountCode = null) => {
  const response = await axios.post(`${API}/payments/paypal/subscribe`, {
    tier_id: tierId,
    billing_interval: billingInterval,
    origin_url: window.location.origin,
    discount_code: discountCode || undefined
  });
  return response.data;
};

// ============= CUSTOM ORDERS API =============

export const submitCustomOrder = async (orderData) => {
  const response = await axios.post(`${API}/custom-orders`, orderData);
  return response.data;
};

export const getCustomOrders = async () => {
  const response = await axios.get(`${API}/admin/custom-orders`);
  return response.data;
};

export const updateCustomOrderStatus = async (orderId, status) => {
  const response = await axios.put(`${API}/admin/custom-orders/${orderId}/status?status=${status}`);
  return response.data;
};

export const reorderPosts = async (section, postIds) => {
  const response = await axios.post(`${API}/admin/posts/reorder`, {
    section,
    post_ids: postIds,
  });
  return response.data;
};

export const createPayPalOrder = async (itemId, itemType) => {
  const response = await axios.post(`${API}/payments/paypal/order`, {
    item_id: itemId,
    item_type: itemType,
    origin_url: window.location.origin
  });
  return response.data;
};

// ============= EMAIL BLAST API =============

export const getBlastRecipients = async (tier) => {
  const response = await axios.get(`${API}/admin/email-blast/recipients`, { params: { tier } });
  return response.data;
};

export const sendBlastTest = async (blastData) => {
  const response = await axios.post(`${API}/admin/email-blast/test`, blastData);
  return response.data;
};

export const sendBlast = async (blastData) => {
  const response = await axios.post(`${API}/admin/email-blast/send`, blastData);
  return response.data;
};

export const getBlastHistory = async () => {
  const response = await axios.get(`${API}/admin/email-blast/history`);
  return response.data;
};

// ===== Direct messages: chat-style actions =====
const dmBase = (conversationId) => `${API}/messages/conversations/${conversationId}`;
export const getDmMessages = async (conversationId, since = null) =>
  (await axios.get(dmBase(conversationId), { params: since ? { since } : {} })).data;
export const sendDm = async (conversationId, content, replyTo = null, image = null) => {
  const params = { content };
  if (replyTo) params.reply_to = replyTo;
  if (image?.url) { params.image_url = image.url; params.image_thumb_url = image.thumbUrl || image.url; }
  return (await axios.post(dmBase(conversationId), null, { params })).data;
};
export const editDm = async (conversationId, messageId, content) =>
  (await axios.put(`${dmBase(conversationId)}/${messageId}`, null, { params: { content } })).data;
export const deleteDm = async (conversationId, messageId) => (await axios.delete(`${dmBase(conversationId)}/${messageId}`)).data;
export const reactToDm = async (conversationId, messageId, emoji) =>
  (await axios.post(`${dmBase(conversationId)}/${messageId}/react`, null, { params: { emoji } })).data;
export const sendDmTyping = async (conversationId) => (await axios.post(`${dmBase(conversationId)}/typing`)).data;
export const getDmTyping = async (conversationId) => (await axios.get(`${dmBase(conversationId)}/typing`)).data;
export const uploadDmImage = async (conversationId, file) => {
  const form = new FormData();
  form.append('file', file);
  return (await axios.post(`${dmBase(conversationId)}/image`, form, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
};

// ===== Comments: chat-style actions =====
export const getCommentMentionable = async (postId) => (await axios.get(`${API}/comments/${postId}/mentionable`)).data;
export const editComment = async (commentId, content, mentionIds = []) =>
  (await axios.put(`${API}/comments/${commentId}`, null, { params: { content, mentions: mentionIds.join(',') } })).data;
export const reactToComment = async (commentId, emoji) =>
  (await axios.post(`${API}/comments/${commentId}/react`, null, { params: { emoji } })).data;
export const pinComment = async (commentId) => (await axios.post(`${API}/comments/${commentId}/pin`)).data;
